/**
 * The listening-blade art (id listening-blade; display name from the game data), hand-made in the sample-page style (replaces the
 * C3 generator skeleton). The art's own look: a night of violet and blossom pink under a pale moon — filled crescent
 * moons (the phase shape, not a slash), a moon disc that is bitten dark, slash crescents with a white outer row and a
 * violet body, moon motes that twinkle, the moon-blight (poison) as small violet crescents dripping on the foe, the charm
 * as two blossom crescents circling the head. No gold, no glyph circle, no beams. Each move is staged anticipation ->
 * strike -> impact -> dissipation; body-moving moves leave split-body afterimages. Original designs.
 *
 * Roles from the data (packages/shared data/arts.ts, data/longMoves/moonSword.ts): ms-moon a poisoning caress, ms-veil
 * the break (charm on a broken guard), ms-longing three thrusts, ms-step a dash through, ms-night lands on a picked spot
 * (charm), ms-kiss the lock (a 5.6 s partner-dance routine, lifesteal), ms-mirror the counter stance, ms-eclipse the
 * ท่าใหญ่ (0.8 s tell, 4.4 s piece). The two long moves' sample times are the data's beats offset by their start
 * (KISS_T, ECLIPSE_T), so fx/longMoon.ts can pin them to the live beats.
 */
import { add, crescent, diamond, hash, lerp, line, px, type G, type V } from '../kit';
import type { Scene } from '../skills';
import { drawRuns, runsOf, type Put } from '../moonDiscCache';
import { bladeArc, C, clamp01, fade, hitStar, level, ringOn, since, strikeAt, tilesPx, type ArtFx } from '../families/swordKit';

// ── Tones ──

const WH = C('white', 8);
const W7 = C('white', 7);
const W6 = C('white', 6);
const V0 = C('violet', 0);
const V1 = C('violet', 1);
const V2 = C('violet', 2);
const V3 = C('violet', 3);
const V4 = C('violet', 4);
const V5 = C('violet', 5);
const V6 = C('violet', 6);
const B2 = C('blossom', 2);
const B3 = C('blossom', 3);
const B4 = C('blossom', 4);
const B5 = C('blossom', 5);
const M4 = C('maroon', 4);

// ── The art's pieces ──

/**
 * A filled crescent moon (a phase, not a cut) at `c`, radius `r`: the disc less a disc of the same size pushed `thin`
 * radii toward `rot` (0.4 fat, 0.9 a sliver). Lit from the upper right, a violet rim on the edge cells.
 */
function moonShape(g: G, c: V, r: number, rot: number, thin: number, lvl: number, warm = false, sy = 1): void {
  if (lvl <= 0 || r < 2) return;
  drawRuns(g, runsOf(`ms|${r}|${rot.toFixed(3)}|${thin}|${warm ? 1 : 0}|${sy}`, (put) => paintMoonShape(put, r, rot, thin, warm, sy)), c[0], c[1], lvl);
}

function paintMoonShape(put: Put, r: number, rot: number, thin: number, warm: boolean, sy: number): void {
  const ox = Math.cos(rot) * r * thin;
  const oy = Math.sin(rot) * r * thin;
  const r2 = r * r;
  const inM = (x: number, y: number): boolean => {
    const yy = y / sy;
    if (x * x + yy * yy > r2) return false;
    const dx = x - ox;
    const dy = yy - oy;
    return dx * dx + dy * dy > r2;
  };
  const lit = warm ? B5 : WH;
  const mid = warm ? B4 : W6;
  const lo = warm ? B3 : V5;
  const rim = warm ? B2 : V3;
  const ry = Math.ceil(r * sy) + 1;
  for (let y = -ry; y <= ry; y++)
    for (let x = -r - 1; x <= r + 1; x++) {
      if (!inM(x, y)) continue;
      const edge = !inM(x + 1, y) || !inM(x - 1, y) || !inM(x, y + 1) || !inM(x, y - 1);
      const k = (x - y) / r;
      put(x, y, edge && r > 3 ? rim : k > 0.45 ? lit : k > -0.35 ? mid : lo);
    }
}

/**
 * The moon disc at `c`, radius `r`, lit from the upper right, with a dark disc slid across it from the right by `bite`
 * (0 none, 1 the full eclipse: a blossom corona round the black). `glow` adds the pale halo ring.
 */
function moonDisc(g: G, c: V, r: number, bite: number, lvl: number, glow = false): void {
  if (lvl <= 0 || r < 2) return;
  const bq = Math.round(clamp01(bite) * 200) / 200;
  drawRuns(g, runsOf(`md|${r}|${bq}|${glow ? 1 : 0}`, (put) => paintMoonDisc(put, r, bq, glow)), c[0], c[1], lvl);
}

function paintMoonDisc(put: Put, r: number, bite: number, glow: boolean): void {
  const bx = (1 - clamp01(bite)) * r * 2.15;
  const by = -(1 - clamp01(bite)) * r * 0.35;
  const r2 = r * r;
  for (let y = -r; y <= r; y++)
    for (let x = -r; x <= r; x++) {
      const d2 = x * x + y * y;
      if (d2 > r2) continue;
      const inBite = bite > 0 && (x - bx) * (x - bx) + (y - by) * (y - by) <= r2;
      const edge = d2 > (r - 1) * (r - 1);
      if (inBite) {
        put(x, y, edge ? V3 : V0);
        continue;
      }
      const k = (x - y) / r;
      // Soft maria: two darker patches on the lit face.
      const sea = (x + r * 0.25) ** 2 + (y + r * 0.1) ** 2 < r2 * 0.08 || (x - r * 0.3) ** 2 + (y - r * 0.35) ** 2 < r2 * 0.05;
      put(x, y, edge ? (k > 0 ? W6 : V4) : sea ? V6 : k > 0.35 ? WH : k > -0.45 ? W7 : V6);
    }
  if (bite >= 0.92) {
    // The corona: a broken blossom ring, the rays every other cell .
    const n = Math.round(r * 7);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const rr = r + 1 + (i % 3 === 0 ? 2 : i % 2);
      put(Math.cos(a) * rr, Math.sin(a) * rr, i % 3 === 0 ? B5 : B3, 3);
    }
  } else if (glow) {
    const n = Math.round(r * 7);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      put(Math.cos(a) * (r + 2), Math.sin(a) * (r + 2), V5, 2);
    }
  }
}

/** isoRing, cached when it is the full ring (a partial ring being drawn on is cheap and stays per-frame). */
function ringC(g: G, x: number, y: number, rx: number, css: string, thick = false): void {
  if (rx < 1) return;
  const r = runsOf(`ring|${rx}|${css}|${thick ? 1 : 0}`, (put) => {
    const n = Math.max(12, Math.round(rx * 7));
    for (let i = 0; i <= n; i++) {
      const a = (i / n) * Math.PI * 2;
      const cx = Math.round(Math.cos(a) * rx);
      const cy = Math.round(Math.sin(a) * rx * 0.5);
      put(cx, cy, css);
      if (thick) put(cx, cy + 1, css);
    }
  });
  drawRuns(g, r, x, y);
}

/** isoDisc with the same look, cached: runs at `lvl`'s dither. */
function discC(g: G, c: V, r: number, fill: string, rim: string | null, lvl: number): void {
  if (r < 1 || lvl <= 0) return;
  const runs = runsOf(`disc|${r}|${fill}|${rim}`, (put) => {
    for (let y = Math.floor(-r / 2); y <= r / 2; y++)
      for (let x = Math.floor(-r); x <= r; x++) {
        const d = Math.hypot(x, y * 2);
        if (d <= r) put(x, y, rim && d > r - 1.6 ? rim : fill);
      }
  });
  drawRuns(g, runs, c[0], c[1], lvl);
}

/**
 * A ground "dark field" that reads at game scale (the pen dithers: level 2 = a hard 50 % checker): a solid `core`, a
 * checker band of `dark` out to the edge, a solid 1-px `rim` and a solid `dark` line just inside it (the outline), no
 * soft glow. Cached per (r, tones). `cap` (0..3) fades it by dropping to the checker (2) or the sparse dots (1).
 */
function fieldC(g: G, c: V, r: number, core: string, dark: string, rim: string, cap: number): void {
  if (r < 2 || cap <= 0) return;
  const runs = runsOf(`field|${r}|${core}|${dark}|${rim}`, (put) => {
    for (let y = Math.floor(-r / 2); y <= r / 2; y++)
      for (let x = Math.floor(-r); x <= r; x++) {
        const d = Math.hypot(x, y * 2);
        if (d > r) continue;
        if (d > r - 1.5) put(x, y, rim, 3);
        else if (d > r - 3) put(x, y, dark, 3);
        else if (d > r * 0.72) put(x, y, dark, 2);
        else put(x, y, core, 3);
      }
  });
  drawRuns(g, runs, c[0], c[1], cap);
}

/**
 * A moon cut: a slash crescent round (cx, cy) from a0 to a1, `w` rows: a white outer row, a violet body, a blossom
 * inner row (the art's cut), squashed by `sy`.
 */
function moonCut(g: G, cx: number, cy: number, r: number, a0: number, a1: number, w: number, lvl: number, sy = 1, shadow = false): void {
  if (lvl <= 0) return;
  const runs = runsOf(`cut|${r}|${a0.toFixed(3)}|${a1.toFixed(3)}|${w}|${sy}|${shadow ? 1 : 0}`, (put) => {
    if (shadow) {
      // A cut out of the dark: a black body with a pale rim.
      crescentRuns(put, r, a0, a1, w, V6, V0, sy);
      crescentRuns(put, r - w + 1, a0 + (a1 - a0) * 0.15, a1, 2, V3, V2, sy);
    } else {
      crescentRuns(put, r, a0, a1, w, WH, V5, sy);
      if (w >= 4) crescentRuns(put, r - w + 1, a0 + (a1 - a0) * 0.2, a1, 2, B4, B3, sy);
    }
  });
  drawRuns(g, runs, cx, cy, lvl);
}

/** kit.crescent (lead end thick) as cells relative to the centre, for the cache. */
function crescentRuns(put: Put, r: number, a0: number, a1: number, w: number, core: string, edge: string, sy: number): void {
  const n = Math.max(8, Math.round(Math.abs(a1 - a0) * r * 1.6));
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    const prof = Math.min(1, u * 1.6) * Math.min(1, (1 - u) * 5);
    const rows = Math.max(1, Math.round(1 + (w - 1) * prof));
    const a = a0 + (a1 - a0) * u;
    for (let k = rows - 1; k >= 0; k--) put(Math.cos(a) * (r - k), Math.sin(a) * (r - k) * sy, k === 0 ? core : edge);
  }
}

/** A moon mote: a twinkling 1-cell star or plus (by frame and index), pale or blossom. */
function mote(g: G, x: number, y: number, e: number, i: number, warm = false): void {
  const big = (e + i) % 4 === 0;
  const css = warm ? (big ? B5 : B4) : big ? WH : V6;
  if (big) diamond(g, x, y, 1, warm ? B3 : V5, css);
  else px(g, x, y, css);
}

/** Motes drifting up round `c` in a band `w` wide, `e` frames old, `n` of them, dithered out over `life` frames. */
function riseMotes(g: G, c: V, w: number, e: number, n: number, seed: number, life: number, warm = false, rise = 1.6): void {
  if (e < 0 || e >= life) return;
  level(g, fade(e, life * 0.6, life * 0.85));
  for (let i = 0; i < n; i++) {
    const h0 = hash(i, seed);
    const age = (e + h0 * life) % life;
    const x = c[0] + (hash(i, seed + 1) - 0.5) * w + Math.sin(age * 0.5 + i) * 1.5;
    const y = c[1] - age * rise - h0 * 6;
    mote(g, x, y, e, i, warm && i % 2 === 0);
  }
  g.globalAlpha = 1;
}

/** Where the moon-blight pins on the foe: x cells off its middle, y in chest heights up. */
const BLIGHT_AT: readonly V[] = [
  [-4, 1.25],
  [4, 0.85],
  [0, 0.5],
  [-5, 0.7],
  [5, 1.45],
];
/** The moon-blight (poison): `n` small violet crescents on the foe, each dripping a violet bead. */
function blight(g: G, s: Scene, n: number, lvl: number): void {
  if (lvl <= 0) return;
  for (let i = 0; i < Math.min(n, BLIGHT_AT.length); i++) {
    const p: V = [s.f[0] + BLIGHT_AT[i]![0], s.f[1] - BLIGHT_AT[i]![1] * s.chest];
    moonShape(g, p, 3, -0.6 + i, 0.55, lvl, false);
    level(g, Math.min(lvl, 2));
    const k = Math.floor(s.t * 12 + i * 3) % 8;
    px(g, p[0] - 1, p[1] + 3 + k, V4);
    if (k > 1) px(g, p[0] - 1, p[1] + 2 + k, V3);
  }
  g.globalAlpha = 1;
}

/** The charm: two blossom crescents circling over the foe's head, facing each other. */
function charm(g: G, s: Scene, e: number, life: number): void {
  if (e < 0 || e >= life) return;
  const head: V = [s.f[0], s.f[1] - s.chest * 2.05];
  const lv = fade(e, life - 6, life - 3);
  for (let i = 0; i < 2; i++) {
    const a = e * 0.5 + i * Math.PI;
    const p: V = [head[0] + Math.cos(a) * 9, head[1] + Math.sin(a) * 3];
    moonShape(g, p, 4, a + Math.PI, 0.6, Math.sin(a) < 0 ? Math.min(lv, 2) : lv, true);
  }
  // A small heart-tone diamond between them, pulsing.
  if (e % 6 < 4) {
    level(g, lv);
    diamond(g, head[0], head[1] - 2, 1, B3, B5);
    g.globalAlpha = 1;
  }
}

/** A drain thread from `a` to `b` (a wavy blossom line) with `n` drops riding it toward `b`; `k` 0..1 how far drawn. */
function drain(g: G, a: V, b: V, phase: number, k: number, lvl: number, n = 3): void {
  if (lvl <= 0 || k <= 0) return;
  level(g, lvl);
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const L = Math.max(1, Math.hypot(dx, dy));
  const nx = -dy / L;
  const ny = dx / L;
  const m = Math.round(L * k);
  for (let i = 0; i <= m; i++) {
    const u = i / L;
    const w = Math.sin(u * 9 + phase) * 2.5 * Math.sin(Math.PI * u);
    px(g, a[0] + dx * u + nx * w, a[1] + dy * u + ny * w, i % 5 === 0 ? B5 : B3);
  }
  if (k >= 1)
    for (let j = 0; j < n; j++) {
      const u = (phase * 0.08 + j / n) % 1;
      const w = Math.sin(u * 9 + phase) * 2.5 * Math.sin(Math.PI * u);
      diamond(g, a[0] + dx * u + nx * w, a[1] + dy * u + ny * w, 1, M4, B5);
    }
  g.globalAlpha = 1;
}

/** Crescent shards flung out of `c` (small filled crescents spinning and falling), `e` frames old. */
function shards(g: G, c: V, e: number, n: number, seed: number, speed: number, life: number, warm = false): void {
  if (e < 0 || e >= life) return;
  const lv = fade(e, life * 0.55, life * 0.8);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + hash(i, seed) * 0.5;
    const v = speed * (0.7 + hash(i, seed + 1) * 0.6);
    const p: V = [c[0] + Math.cos(a) * v * (e + 1), c[1] + Math.sin(a) * v * (e + 1) * 0.7 + 0.25 * e * e];
    moonShape(g, p, e < 3 ? 3 : 2, a + e * 0.9, 0.6, lv, warm && i % 2 === 0);
  }
}

/** The blade's moon glint before a blow: a small crescent moon forming on the tip, a white diamond at full. */
function tipMoon(g: G, s: Scene, t0: number, t1: number, warm = false): void {
  if (s.t < t0 || s.t >= t1) return;
  const { tip } = s.limbs(s.t);
  const k = clamp01((s.t - t0) / Math.max(0.05, t1 - t0));
  moonShape(g, [tip[0], tip[1] - 4], 2 + Math.round(k * 3), -0.6 + k, 0.55, 3, warm);
  level(g, 3);
  diamond(g, tip[0], tip[1], k < 0.6 ? 1 : 2, V5, WH);
  g.globalAlpha = 1;
}

// ── 1. ms-moon: a caressing stroke, a lying moon-smile cut across the foe, moonlight film, the blight pinned ──

const MO_HIT = 0.55;
const moon: ArtFx = {
  id: 'moon-moon',
  skill: 'ms-moon',
  th: 'ms-moon',
  on: true,
  note: 'จันทร์เสี้ยวเล็กก่อตัวที่ปลายกระบี่ -> ลูบกระบี่ผ่านตัวเป้าเป็นรอยจันทร์เสี้ยวนอน (ขอบขาว เนื้อม่วง ซับชมพู) -> แสงจันทร์ร่วงเป็นละอองบนตัวเป้า จันทร์เสี้ยวม่วงเล็กเกาะเป้าหยดพิษ (พิษจันทร์มัว) -> จาง',
  weapon: 'sword',
  acts: [{ m: 'wxCleave', t0: 0.2, t1: 0.9 }],
  hits: [MO_HIT],
  ghosts: [0.46, 0.52],
  stepIn: 6,
  pre: 0.3,
  hit: MO_HIT,
  end: 1.8,
  afterimage: 'port',
  over(g, s) {
    tipMoon(g, s, 0.2, 0.46);
    bladeArc(g, s, 0.4, 0.72, 'violet', 4);
    const e = since(s.t, MO_HIT);
    if (e < 0) return;
    const c = strikeAt(s);
    // The caress: a long lying crescent (a moon's smile) swept across the foe, thinning from the leading end.
    if (e < 5) moonCut(g, c[0] - 2, c[1] - 14, 30, Math.PI * 0.92, Math.PI * 0.08, e < 2 ? 7 : 4, fade(e, 2, 4), 0.55);
    // The moonlight film settling on the foe.
    riseMotes(g, [c[0], c[1] + 4], 22, e - 1, 8, 31, 12, false, -0.8);
    if (e >= 2) blight(g, s, 1, e < 3 ? 2 : fade(e, 16, 19));
    hitStar(g, s, c, e, 'violet', 1.1);
  },
};

// ── 2. ms-veil (break): a moonlight veil hides the blade, the thrust tears through it, the guard breaks into crescents ──

const VE_HIT = 0.8;
/** The veil: a curtain of pale strands hung from a crescent, `x0` its middle, `apart` cells each half has swung out. */
function veil(g: G, s: Scene, x0: number, lvl: number, sway: number, apart: number, drop: number): void {
  if (lvl <= 0) return;
  const top = Math.round(s.a[1] - s.chest * 2.3);
  const h = Math.round(s.chest * 2.1);
  level(g, lvl);
  for (let i = -6; i <= 6; i++) {
    if (i === 0 && apart > 0) continue;
    const side = i < 0 ? -1 : 1;
    const xo = x0 + i * 2 + side * apart;
    const len = h - Math.abs(i) * 2;
    for (let y = 0; y < len; y++) {
      const x = xo + Math.sin(y * 0.18 + sway + i) * 1.4 * (y / len);
      const css = y < 2 ? V6 : (y + i) % 7 === 0 ? WH : i % 2 ? V5 : V4;
      if (lvl < 3 && (x + y) % 2) continue;
      px(g, x, top + y + drop * (0.3 + Math.abs(i) * 0.1), css);
    }
  }
  g.globalAlpha = 1;
  if (apart <= 0) moonShape(g, [x0, top - 3], 6, -Math.PI / 2, 0.6, lvl);
}
const veilFx: ArtFx = {
  id: 'moon-veil',
  skill: 'ms-veil',
  th: 'ms-veil',
  on: true,
  note: 'ท่าทำลาย: ม่านแสงจันทร์ (เส้นไหมม่วงขาว แขวนจากจันทร์เสี้ยว) คลี่ลงหน้าตัว ซ่อนกระบี่ไว้หลังม่าน (ตั้งท่า 0.45 วิ) -> แทงทะลุม่าน ม่านขาดแยกสองข้างแล้วร่วง -> โล่จันทร์บนเป้าแตกเป็นเศษจันทร์เสี้ยวกระจาย (การ์ดแตก) -> จันทร์เสี้ยวชมพูสองดวงวนบนหัวเป้า (มนตร์จันทร์มัว)',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: 0.6 },
    { m: 'wxThrust', t0: 0.6, t1: 1.15 },
  ],
  hits: [VE_HIT],
  big: VE_HIT,
  ghosts: [0.72, 0.77],
  stepIn: 8,
  cast: [0.05, 0.6],
  pre: 0.6,
  hit: VE_HIT,
  end: 2.4,
  afterimage: 'port',
  beats: [
    { t: 0.08, th: 'ม่านจันทร์' },
    { t: VE_HIT, th: 'แทงทะลุ' },
    { t: VE_HIT + 0.3, th: 'มนตร์' },
  ],
  over(g, s) {
    const x0 = Math.round(s.a[0] + s.d[0] * 16);
    const e = since(s.t, VE_HIT);
    // The veil unrolling down over the wind-up, then torn apart on the thrust and falling.
    if (e < 0 && s.t >= 0.08) {
      const k = clamp01((s.t - 0.08) / 0.3);
      veil(g, s, x0, k < 0.3 ? 1 : k < 0.7 ? 2 : 3, s.t * 6, 0, -(1 - k) * 20);
    } else if (e >= 0 && e < 10) veil(g, s, x0, fade(e, 4, 7), s.t * 6, 2 + e * 2, e * e * 0.5);
    // The guard: a pale crescent shield stands on the foe through the wind-up, cracks and breaks on the blow.
    const c = strikeAt(s);
    if (e < 0 && s.t >= 0.3) moonShape(g, [c[0] - 6, c[1] - 2], 13, 0, 0.45, 2);
    if (e >= 0 && e < 2) {
      moonShape(g, [c[0] - 6, c[1] - 2], 13, 0, 0.45, 3);
      level(g, 3);
      line(g, [c[0] - 14, c[1] - 12], [c[0] - 2, c[1] + 8], V0);
      g.globalAlpha = 1;
    }
    tipMoon(g, s, 0.45, VE_HIT - 0.05);
    bladeArc(g, s, VE_HIT - 0.12, VE_HIT + 0.12, 'violet', 4);
    if (e < 0) return;
    // The thrust's line through the torn veil.
    if (e < 4) {
      level(g, fade(e, 2, 3));
      const y = c[1];
      for (let k = 0; k < (e < 2 ? 3 : 1); k++) line(g, [x0 - 6, y + k], [c[0] + 18, y + k], k === 0 ? WH : k === 1 ? V5 : B3);
      g.globalAlpha = 1;
    }
    shards(g, [c[0] - 6, c[1] - 2], e - 1, 8, 61, 3.2, 12);
    hitStar(g, s, c, e, 'blossom', 1.5);
    charm(g, s, e - 4, 22);
  },
};

// ── 3. ms-longing: three thrusts, each pins a small moon, one pink thread runs unbroken through all three ──

const LO_HITS = [0.5, 0.68, 0.86] as const;
const LO_Y = [1.35, 1.0, 0.65] as const;
const loPin = (s: Scene, i: number): V => [s.f[0] - s.d[0] * 2 + (i - 1) * 3, s.f[1] - s.chest * LO_Y[i]!];
const longing: ArtFx = {
  id: 'moon-longing',
  skill: 'ms-longing',
  th: 'ms-longing',
  on: true,
  note: 'แทงสามครั้งสามระดับ (บน กลาง ล่าง) แต่ละครั้งแสงแทงขาวขอบชมพู ปักจันทร์เสี้ยวเล็กไว้บนตัวเป้า -> ด้ายชมพูเส้นเดียวร้อยผ่านทั้งสามจุดไม่ขาดสาย (คิดถึงไม่ขาด) ดึงตึง -> ด้ายหย่อน ปลิวเป็นละอองชมพู พิษจันทร์มัวสามชั้น',
  weapon: 'sword',
  acts: [
    { m: 'wxThrust', t0: 0.3, t1: 0.58 },
    { m: 'wxThrust', t0: 0.58, t1: 0.76 },
    { m: 'wxThrust', t0: 0.76, t1: 1.05 },
  ],
  hits: [...LO_HITS],
  big: LO_HITS[2],
  ghosts: [0.46, 0.64, 0.82],
  stepIn: 4,
  pre: 0.35,
  hit: LO_HITS[0],
  end: 2.1,
  afterimage: 'port',
  beats: [
    { t: LO_HITS[0], th: 'แทงหนึ่ง' },
    { t: LO_HITS[1], th: 'แทงสอง' },
    { t: LO_HITS[2], th: 'แทงสาม' },
  ],
  over(g, s) {
    tipMoon(g, s, 0.25, LO_HITS[0] - 0.04, true);
    const { tip } = s.limbs(s.t);
    let pinned = 0;
    for (let i = 0; i < 3; i++) {
      const e = since(s.t, LO_HITS[i]!);
      if (e < 0) continue;
      pinned++;
      const p = loPin(s, i);
      // The thrust: a straight lance of light from the tip into the pin.
      if (e < 3) {
        level(g, e < 2 ? 3 : 2);
        const from: V = [p[0] - 34, p[1] + (i - 1) * 2];
        for (let k = 0; k < (e < 1 ? 3 : 2); k++) line(g, [from[0] + k * 4, from[1] + k], [p[0] + 6, p[1] + k], k === 0 ? WH : k === 1 ? B4 : B2);
        g.globalAlpha = 1;
      }
      hitStar(g, s, p, e, 'blossom', 0.85 + i * 0.15);
    }
    // The pins and the thread through them (and back to the blade while the thrusts last).
    const eL = since(s.t, LO_HITS[2]);
    if (pinned > 0 && eL < 16) {
      const lv = eL < 6 ? 3 : fade(eL - 6, 5, 8);
      for (let i = 0; i < pinned; i++) moonShape(g, loPin(s, i), 4, Math.PI + 0.4 * (i - 1), 0.55, lv, true);
      level(g, lv);
      const slack = eL >= 4 ? Math.min(6, eL - 3) : 0;
      for (let i = 1; i < pinned; i++) {
        const a = loPin(s, i - 1);
        const b = loPin(s, i);
        const m: V = [(a[0] + b[0]) / 2 + 4 + slack, (a[1] + b[1]) / 2 + slack * 0.5];
        line(g, a, m, B4);
        line(g, m, b, B4);
      }
      if (eL < 0 || eL < 3) line(g, loPin(s, pinned - 1), tip, B3);
      g.globalAlpha = 1;
    }
    // The thread blown away as petals of light.
    if (eL >= 8) riseMotes(g, [s.f[0] + 4, s.f[1] - s.chest * 0.9], 18, eL - 8, 8, 77, 10, true, 1.2);
    if (pinned > 0) blight(g, s, pinned, eL > 14 ? fade(eL - 14, 3, 5) : 3);
  },
};

// ── 4. ms-step: the body sinks into its moon shadow, a dash through the foe on a crescent wake, a cut in passing ──

const STEP = 100;
const ST_GO = 0.4;
const ST_HIT = 0.55;
const step: ArtFx = {
  id: 'moon-step',
  skill: 'ms-step',
  th: 'ms-step',
  on: true,
  note: 'จันทร์เสี้ยวเงาม่วงเข้มก่อตัวหลังร่าง ร่างจมเข้าเงา (ตั้งท่า) -> พุ่งทะลุผ่านเป้าบนรอยจันทร์เสี้ยวยาวแบนเลียดพื้น เงาร่างแยก -> รอยตัดขาวม่วงผ่ากลางตัวเป้าตอนผ่าน พิษจันทร์มัว -> รอยเท้าจันทร์เสี้ยวบนพื้นจาง',
  weapon: 'sword',
  acts: [
    { m: 'wxThrust', t0: 0.2, t1: 0.7 },
    { m: 'wxCleave', t0: 0.5, t1: 1.0 },
  ],
  hits: [ST_HIT],
  big: ST_HIT,
  ghosts: [0.44, 0.48, 0.52],
  cast: [0.05, ST_GO],
  pre: ST_GO,
  hit: ST_HIT,
  end: 2.0,
  dash: STEP,
  afterimage: 'dash',
  move(s, t) {
    const k0 = clamp01((t - ST_GO) / 0.22);
    const k = 1 - (1 - k0) * (1 - k0);
    const back = 1 - clamp01((t - 1.7) / 0.3);
    return [s.d[0] * STEP * k * back, s.d[1] * STEP * k * back];
  },
  under(g, s) {
    // The moon-shadow path: crescent footprints lying along the dash, fading from the start.
    const e = since(s.t, ST_GO);
    if (e < 0 || e > 18) return;
    const k = clamp01((s.t - ST_GO) / 0.22);
    for (let i = 0; i < 6; i++) {
      const u = (i + 0.5) / 6;
      if (u > k) break;
      const p = add(s.a, s.d, STEP * u);
      moonShape(g, [p[0], p[1] + 1], 5, -Math.PI / 2, 0.6, fade(e - i, 8, 13), false, 0.45);
    }
  },
  over(g, s) {
    const b = s.body(s.t);
    // The wind-up: a dark crescent rising behind the back, the body sinking into it.
    if (s.t >= 0.05 && s.t < ST_GO + 0.05) {
      const k = clamp01((s.t - 0.05) / 0.3);
      level(g, 3);
      crescent(g, b[0] - s.d[0] * 4, b[1] - s.chest * 1.1, Math.round(8 + 8 * k), Math.PI * 0.6, Math.PI * 1.45, 4, V5, V1);
      g.globalAlpha = 1;
    }
    // The wake: a long flat crescent from the start to the body, the fresh end white.
    const e = since(s.t, ST_GO);
    if (e >= 0 && e < 8) {
      const L = Math.max(8, b[0] - s.a[0]);
      const mid: V = [s.a[0] + L / 2, b[1] - s.chest * 0.8];
      moonCut(g, mid[0], mid[1] - L * 0.5 * 0.25 + 10, L / 2, Math.PI * 0.95, Math.PI * 0.05, e < 3 ? 5 : 3, fade(e, 4, 6), 0.25);
      riseMotes(g, mid, L, e, 10, 91, 12, false, 0.9);
    }
    // The cut in passing: a flat white line through the foe, opened downward into a crescent.
    const eh = since(s.t, ST_HIT);
    if (eh >= 0 && eh < 6) {
      const c = strikeAt(s, 1.0);
      moonCut(g, c[0] + 2, c[1] - 18, 22, Math.PI * 0.8, Math.PI * 0.2, eh < 2 ? 6 : 3, fade(eh, 3, 5), 0.8);
    }
    if (eh >= 2) blight(g, s, 1, eh < 3 ? 2 : fade(eh, 15, 18));
    hitStar(g, s, strikeAt(s), eh, 'violet', 1.3);
  },
};

// ── 5. ms-night: night thrown over the picked spot — a dark disc of stars, curtains of night, a crescent round the rim ──

const NI_R = Math.min(60, tilesPx(2.4));
const NI_LAND = 0.7;
const night: ArtFx = {
  id: 'moon-night',
  skill: 'ms-night',
  th: 'ms-night',
  on: true,
  note: 'วงม่วงวาดบอกจุดตก หมุนกระบี่ ผ้าราตรีสีม่วงเข้มสะบัดออกจากคมกระบี่โค้งข้ามไปยังจุด -> ราตรีห่มลงเป็นแผ่นมืดเต็มวง ดาวกะพริบ ม่านราตรีตั้งขึ้นรอบขอบ จันทร์เสี้ยวกวาดรอบวง -> ทุกคนในวงติดมนตร์จันทร์มัว (จันทร์ชมพูสองดวงวนหัว) -> ม่านสลายเป็นดาวลอยขึ้น',
  weapon: 'sword',
  acts: [
    { m: 'wxCleave', t0: 0.1, t1: 0.5 },
    { m: 'wxRise', t0: 0.5, t1: 0.62 },
    { m: 'wxCleave', t0: 0.62, t1: 1.1 },
  ],
  hits: [NI_LAND],
  big: NI_LAND,
  ghosts: [0.3, 0.4],
  stepIn: 3,
  pre: 0.25,
  hit: NI_LAND,
  end: 2.8,
  afterimage: 'port',
  under(g, s) {
    if (s.t >= 0.1 && s.t < NI_LAND + 0.1) {
      level(g, 3);
      ringOn(g, s.f, NI_R, -Math.PI / 2, (s.t - 0.1) / 0.5, V4, true);
      g.globalAlpha = 1;
    }
    const e = since(s.t, NI_LAND);
    if (e < 0 || e > 24) return;
    // The night: a dark disc spreading out from the spot, stars in it, the rim violet.
    const r = Math.round(NI_R * Math.min(1, 0.35 + e * 0.22));
    fieldC(g, s.f, r, V1, V0, V5, e < 16 ? 3 : 2);
    level(g, fade(e, 16, 21));
    for (let i = 0; i < 26; i++) {
      const a = hash(i, 120) * Math.PI * 2;
      const rr = Math.sqrt(hash(i, 121)) * r * 0.92;
      if ((e + i) % 5 === 0) continue;
      mote(g, s.f[0] + Math.cos(a) * rr, s.f[1] + Math.sin(a) * rr * 0.5, e, i);
    }
    g.globalAlpha = 1;
    if (e < 6) {
      level(g, fade(e, 3, 5));
      ringC(g, s.f[0], s.f[1], Math.round(NI_R * (0.3 + 0.75 * (e / 5))), WH, e < 3);
      g.globalAlpha = 1;
    }
  },
  over(g, s) {
    const { tip } = s.limbs(s.t);
    // The mantle: a dark violet cloth flung off the blade, arcing over to the spot.
    if (s.t >= 0.3 && s.t < NI_LAND) {
      const k = clamp01((s.t - 0.3) / (NI_LAND - 0.3));
      const head = lerp(tip, [s.f[0], s.f[1] - s.chest * 1.2], k);
      level(g, 3);
      for (let j = 0; j < 14; j++) {
        const u = j / 14;
        const q = lerp(tip, head, 1 - u * 0.5);
        const lift = Math.sin(Math.PI * k * (1 - u * 0.5)) * 34;
        const w = Math.round(2 + 5 * (1 - u));
        for (let r = 0; r < w; r++) px(g, q[0] - u * 6, q[1] - lift + r - w / 2 + Math.sin(u * 8 + s.t * 20), r === 0 ? V5 : r === w - 1 ? V0 : V2);
      }
      g.globalAlpha = 1;
    }
    const e = since(s.t, NI_LAND);
    if (e < 0) return;
    // Curtains of night standing round the rim (front half drawn over), then rising away as stars.
    if (e < 16) {
      const hgt = Math.round(Math.min(1, (e + 1) / 3) * 30);
      const lv = fade(e, 9, 13);
      level(g, lv);
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2 + 0.1;
        if (Math.sin(a) < 0) continue;
        const x = s.f[0] + Math.cos(a) * NI_R;
        const y = s.f[1] + Math.sin(a) * NI_R * 0.5;
        const h = hgt - Math.round(hash(i, 130) * 8) - (e > 8 ? (e - 8) * 3 : 0);
        for (let k = 0; k < h; k++) px(g, x + Math.sin(k * 0.3 + e + i) * 0.8, y - k, k > h - 3 ? V5 : (k + i) % 6 === 0 ? V4 : V2);
      }
      g.globalAlpha = 1;
    }
    // The crescent sweeping round the rim.
    if (e < 10) {
      const a0 = -Math.PI / 2 + e * 0.75;
      moonCut(g, s.f[0], s.f[1] - s.chest * 0.4, Math.round(NI_R * 0.85), a0, a0 + 1.7, e < 6 ? 5 : 3, fade(e, 6, 8), 0.5);
    }
    riseMotes(g, [s.f[0], s.f[1] - 12], NI_R * 1.6, e - 10, 14, 141, 14);
    hitStar(g, s, strikeAt(s), e, 'violet', 1.2);
    charm(g, s, e - 3, 24);
  },
};

// ── 6. ms-kiss (the lock routine, 5.6 s): a partner dance under a crescent, the drain kiss, the spin-away crescent cut ──

/** The routine's start on the page (s): the 0.35 s lock wind-up before it. The beats are the data's (+ KISS_T0). */
const KISS_T0 = 0.4;
const K_TAKE = KISS_T0;
const K_TURN = [KISS_T0 + 0.5, KISS_T0 + 1.4] as const;
const K_DIP = KISS_T0 + 2.2;
const K_TOUCH = KISS_T0 + 3.0;
const K_SPIN = KISS_T0 + 4.0;
const K_BOW = KISS_T0 + 4.6;
const K_STANCE = KISS_T0 + 5.1;
const K_END = KISS_T0 + 5.6;
/** The blows (the rules spread a beat's blows over the first 0.6 of it). */
const K_TURN_HIT = [K_TURN[0] + 0.3 * 0.9, K_TURN[1] + 0.3 * 0.8] as const;
const K_TOUCH_HIT = [K_TOUCH + 0.1, K_TOUCH + 0.3, K_TOUCH + 0.5] as const;
const K_SPIN_HIT = K_SPIN + 0.3 * 0.6;
const kiss: ArtFx = {
  id: 'moon-kiss',
  skill: 'ms-kiss',
  th: 'ms-kiss',
  on: true,
  note: 'ท่ายาว (ล็อก 5.6 วิ): มือเปล่ายื่นเชิญ จันทร์เสี้ยวชมพูลอยเหนือฝ่ามือ กระบี่ต่ำหลังหลัง -> จูงมือ เงาจันทร์เสี้ยวใหญ่ทอดบนพื้นใต้คู่เต้น -> หมุนลอดแขนสองรอบ รอยจันทร์เสี้ยววนรอบเป้า -> โน้มตัว (dip) จันทร์เต็มดวงขึ้นเหนือคู่ ละอองจันทร์ลอย -> ปลายกระบี่จุมพิตสามครั้ง ด้ายชมพูดูดเลือดไหลกลับเข้าตัวผู้ใช้ -> หมุนตัวออกฟันจันทร์เสี้ยวใหญ่ เป้ากระเด็น -> โค้งคำนับ เป้าติดมนตร์จันทร์มัว',
  weapon: 'sword',
  acts: [
    { m: 'wxLockSword', t0: 0.02, t1: KISS_T0 },
    { m: 'wxPalm', t0: K_TAKE, t1: K_TURN[0] },
    { m: 'wxCleave', t0: K_TURN[0], t1: K_TURN[0] + 0.6 },
    { m: 'wxCleave', t0: K_TURN[1], t1: K_TURN[1] + 0.6 },
    { m: 'wxPower', t0: K_DIP, t1: K_TOUCH },
    { m: 'wxThrust', t0: K_TOUCH, t1: K_TOUCH + 0.25 },
    { m: 'wxThrust', t0: K_TOUCH + 0.25, t1: K_TOUCH + 0.5 },
    { m: 'wxThrust', t0: K_TOUCH + 0.5, t1: K_SPIN },
    { m: 'wxCleave', t0: K_SPIN, t1: K_BOW },
    { m: 'wxBuff', t0: K_BOW, t1: K_END },
  ],
  hits: [...K_TURN_HIT, ...K_TOUCH_HIT, K_SPIN_HIT],
  big: K_SPIN_HIT,
  ghosts: [K_TURN[0] + 0.1, K_TURN[0] + 0.2, K_TURN[1] + 0.1, K_TURN[1] + 0.2, K_SPIN + 0.05, K_SPIN + 0.12],
  stepIn: 4,
  cast: [0.02, KISS_T0],
  pre: KISS_T0,
  hit: K_TURN_HIT[0],
  end: K_END,
  afterimage: 'port',
  beats: [
    { t: 0.02, th: 'เชิญ' },
    { t: K_TAKE, th: 'จูงมือ' },
    { t: K_TURN[0], th: 'หมุนลอด 1' },
    { t: K_TURN[1], th: 'หมุนลอด 2' },
    { t: K_DIP, th: 'โน้ม' },
    { t: K_TOUCH, th: 'จุมพิต x3' },
    { t: K_SPIN, th: 'หมุนออกฟัน' },
    { t: K_BOW, th: 'คำนับ' },
  ],
  under(g, s) {
    // The dance floor: a great lying crescent shadow under the pair from the take to the bow.
    if (s.t >= K_TAKE && s.t < K_STANCE) {
      const mid: V = [(s.a[0] + s.f[0]) / 2, s.a[1] + 2];
      const k = clamp01((s.t - K_TAKE) / 0.3);
      const lv = s.t < K_TAKE + 0.15 ? 1 : s.t > K_BOW ? fade(since(s.t, K_BOW), 2, 5) : 2;
      const r = Math.round(20 + 34 * k);
      moonShape(g, mid, r, -Math.PI / 2, 0.55, lv, false, 0.42);
    }
    // The spin-away cut's mark on the ground.
    const e = since(s.t, K_SPIN_HIT);
    if (e >= 0 && e < 14) moonCut(g, s.f[0], s.f[1] - 4, 28, Math.PI * 0.1, Math.PI * 0.9, 3, fade(e, 7, 11), 0.4);
  },
  over(g, s) {
    const { tip, handB } = s.limbs(s.t);
    // The invitation: a small blossom moon over the free hand, the blade's low glint.
    if (s.t >= 0.02 && s.t < K_TAKE + 0.2) {
      const k = clamp01((s.t - 0.02) / 0.3);
      moonShape(g, [handB[0], handB[1] - 6 - k * 3], 2 + Math.round(k * 2), -0.5, 0.55, 3, true);
      level(g, 2);
      diamond(g, tip[0], tip[1], 1, V5, WH);
      g.globalAlpha = 1;
    }
    // The two turns: crescent trails circling the foe at waist height, the back half dim.
    for (let j = 0; j < 2; j++) {
      const t0 = K_TURN[j]!;
      const e = since(s.t, t0);
      if (e < 0 || e >= 10) continue;
      const a0 = (j ? -1 : 1) * (e * 0.9) + Math.PI * 0.5;
      const cy = s.f[1] - s.chest * (j ? 1.25 : 0.85);
      moonCut(g, s.f[0], cy, 25, a0, a0 + (j ? -2.4 : 2.4), e < 5 ? 5 : 3, fade(e, 5, 8), 0.45);
      hitStar(g, s, strikeAt(s, j ? 1.2 : 0.9), since(s.t, K_TURN_HIT[j]!), 'violet', 0.9);
      // A drop of the drain on each blow.
      const ed = since(s.t, K_TURN_HIT[j]!);
      if (ed >= 0 && ed < 6) {
        level(g, fade(ed, 3, 5));
        const p = lerp(strikeAt(s), [s.a[0], s.a[1] - s.chest], ed / 5);
        diamond(g, p[0], p[1] - Math.sin((ed / 5) * Math.PI) * 8, 1, M4, B5);
        g.globalAlpha = 1;
      }
    }
    // The dip: the full moon rising over the pair, motes drifting up.
    if (s.t >= K_DIP && s.t < K_BOW + 0.3) {
      const k = clamp01((s.t - K_DIP) / 0.4);
      const mid: V = [(s.a[0] + s.f[0]) / 2, s.f[1] - s.chest * 2.3 - k * 4];
      const lv = s.t < K_DIP + 0.1 ? 1 : s.t > K_BOW ? 1 : s.t > K_SPIN ? 2 : 3;
      moonDisc(g, mid, Math.round(6 + 6 * k), 0, lv, true);
      riseMotes(g, [mid[0], s.f[1] - 4], 70, since(s.t, K_DIP), 12, 151, 30, true, 1.1);
    }
    // The three touches: a blossom glint on the tip, a drain thread from the foe back into the caster.
    if (s.t >= K_TOUCH && s.t < K_SPIN + 0.1) {
      const b = s.body(s.t);
      const from: V = [s.f[0] - s.d[0] * 2, s.f[1] - s.chest * 1.15];
      const to: V = [b[0], b[1] - s.chest * 1.1];
      const k = clamp01((s.t - K_TOUCH) / 0.15);
      drain(g, from, to, s.t * 16, k, s.t > K_SPIN - 0.1 ? 2 : 3);
      for (let i = 0; i < 3; i++) {
        const e = since(s.t, K_TOUCH_HIT[i]!);
        if (e < 0 || e >= 3) continue;
        moonShape(g, [from[0] - 1, from[1] - 3 + i * 3], 4 - e, Math.PI, 0.5, 3, true);
        hitStar(g, s, from, e, 'blossom', 0.8);
      }
      // The caster filled: blossom motes rising off it.
      riseMotes(g, to, 16, since(s.t, K_TOUCH_HIT[0]), 6, 161, 14, true, 1.4);
    }
    // The spin-away: one great crescent cut round the turn, shards and petals out of the foe.
    bladeArc(g, s, K_SPIN - 0.05, K_SPIN_HIT + 0.1, 'violet', 5);
    const e = since(s.t, K_SPIN_HIT);
    if (e >= 0) {
      const c = strikeAt(s);
      if (e < 6) moonCut(g, c[0] - 10, c[1] + 2, 34, -Math.PI * 0.55, Math.PI * 0.35, e < 2 ? 9 : 5, fade(e, 3, 5), 0.9);
      shards(g, c, e - 1, 10, 171, 3.4, 13, true);
      hitStar(g, s, c, e, 'violet', 1.7);
    }
    // The bow: the foe charmed (the routine's end status).
    charm(g, s, since(s.t, K_BOW - 0.1), 26);
  },
};

// ── 7. ms-mirror: the moon on still water — ripples under the feet, a standing crescent mirror, the body wavering ──

const MI_SET = 0.8;
const mirror: ArtFx = {
  id: 'moon-mirror',
  skill: 'ms-mirror',
  th: 'ms-mirror',
  on: true,
  note: 'น้ำนิ่งสีม่วงแผ่ใต้เท้า วงระลอกขาวสามวง เงาจันทร์เสี้ยวนอนสั่นไหวบนผิวน้ำ -> จันทร์เสี้ยวใหญ่ตั้งข้างตัวเหมือนกระจก ร่างพร่าเป็นริ้วคลื่น (ตั้งรับรอ 8 วิ) -> จันทร์เสี้ยวเล็กหดเข้าวนรอบไหล่รอรับท่า ผิวน้ำจางลง',
  weapon: 'sword',
  acts: [{ m: 'wxBuff', t0: 0.1, t1: 1.9 }],
  hits: [],
  pre: 0.1,
  hit: MI_SET,
  end: 2.9,
  afterimage: null,
  under(g, s) {
    const b = s.body(s.t);
    const e = since(s.t, 0.12);
    if (e < 0 || e > 30) return;
    const lv = e < 20 ? 2 : fade(e - 20, 5, 9) - 1;
    discC(g, b, Math.min(28, 8 + e * 3), V2, V4, lv);
    for (let k = 0; k < 3; k++) {
      const ee = e - k * 4;
      if (ee < 0 || ee > 10) continue;
      level(g, fade(ee, 5, 8));
      ringC(g, b[0], b[1], Math.round(6 + ee * 3), k === 0 ? WH : V5);
    }
    g.globalAlpha = 1;
    // The moon's reflection: a lying crescent on the water in front of the feet, its rows wavering.
    if (e >= 3 && e < 26) {
      const p: V = [b[0] + s.d[0] * 12, b[1] + 3];
      moonShape(g, [p[0] + (e % 3) - 1, p[1]], 7, -Math.PI / 2 + 0.4, 0.55, e < 20 ? 3 : 2, false, 0.4);
    }
  },
  over(g, s) {
    const b = s.body(s.t);
    // The mirror: a tall crescent standing beside the body, then shrinking into a small moon circling the shoulders.
    const w = since(s.t, 0.3);
    if (w >= 0 && w < 8) {
      const k = clamp01((w + 1) / 4);
      moonShape(g, [b[0] + s.d[0] * 13, b[1] - s.chest * 1.2], Math.round(6 + 9 * k), Math.PI, 0.55, w < 6 ? 3 : 2);
    }
    // The body wavering: short pale ripple lines across it.
    if (w >= 2 && w < 14) {
      level(g, w < 10 ? 2 : 1);
      for (let i = 0; i < 4; i++) {
        const y = b[1] - s.chest * (0.4 + i * 0.45);
        const off = Math.sin(w * 0.9 + i * 1.7) * 3;
        line(g, [b[0] - 7 + off, y], [b[0] + 7 + off, y], i % 2 ? V6 : WH);
      }
      g.globalAlpha = 1;
    }
    const e = since(s.t, MI_SET + 0.1);
    if (e >= 0 && e < 22) {
      const a = e * 0.45;
      moonShape(g, [b[0] + Math.cos(a) * 12, b[1] - s.chest * 1.4 + Math.sin(a) * 4], 4, a + Math.PI, 0.55, Math.sin(a) < 0 ? 2 : fade(e, 16, 19));
    }
  },
};

// ── 8. ms-eclipse (ท่าใหญ่): the sky dims, six cuts out of the dark, the moon bitten black, one pale crescent ──

/** The piece's start on the page (s): the 0.8 s tell before it. The beats are the data's (+ ECL_T0). */
const ECL_T0 = 0.85;
const E_R = Math.min(78, tilesPx(3.4));
const E_DIM = ECL_T0;
const E_BLINK = [ECL_T0 + 0.35, ECL_T0 + 0.8, ECL_T0 + 1.25] as const;
const E_BLINK_HIT = [
  [E_BLINK[0] + 0.0675, E_BLINK[0] + 0.2025],
  [E_BLINK[1] + 0.0675, E_BLINK[1] + 0.2025],
  [E_BLINK[2] + 0.0825, E_BLINK[2] + 0.2475],
] as const;
const E_DISC = ECL_T0 + 1.8;
const E_BITE = ECL_T0 + 2.3;
const E_BITE_HIT = [E_BITE + 0.06, E_BITE + 0.18, E_BITE + 0.3] as const;
const E_CRES = ECL_T0 + 2.9;
const E_CRES_HIT = E_CRES + 0.15;
const E_RETURN = ECL_T0 + 3.4;
const E_FADE = ECL_T0 + 3.9;
const E_END = ECL_T0 + 4.4;
/** Where the caster stands through the blinks (behind, in front, behind), then back for the crescent. */
function eclipseMove(s: Scene, t: number): V {
  const fx = s.f[0] - s.a[0];
  const fy = s.f[1] - s.a[1];
  if (t < E_BLINK[0]) return [0, 0];
  if (t < E_BLINK[1]) return [fx + s.d[0] * 24, fy + s.d[1] * 24];
  if (t < E_BLINK[2]) return [fx - s.d[0] * 22, fy - s.d[1] * 22 - 0];
  if (t < E_DISC) return [fx + s.d[0] * 24, fy + s.d[1] * 24];
  if (t < E_FADE) return [fx - s.d[0] * 26, fy - s.d[1] * 26];
  const back = 1 - clamp01((t - E_FADE) / 0.3);
  return [(fx - s.d[0] * 26) * back, (fy - s.d[1] * 26) * back];
}
/** The moon's place over the foe. */
const moonAt = (s: Scene): V => [s.f[0] - 8, s.f[1] - s.chest * 2.1];
const eclipse: ArtFx = {
  id: 'moon-eclipse',
  skill: 'ms-eclipse',
  th: 'ms-eclipse',
  on: true,
  note: 'ท่าใหญ่ ~5.2 วิ: ยกกระบี่ จันทร์ดวงเล็กขึ้นเหนือปลายกระบี่ วงพื้นที่ม่วงวาดรอบตัว (ตั้งท่า 0.8 วิ) -> ฟ้ามืด แผ่นราตรีคลุมวง ดาวกะพริบ -> หายตัวเป็นควันเงาจันทร์ ปรากฏหลัง-หน้า-หลังเป้า ฟันเงาจันทร์ดำขอบซีดสามครั้ง (หกคม) -> จันทร์เต็มดวงขึ้นเหนือเป้า -> เงามืดกัดจันทร์สามคำ เศษเงาร่วงใส่เป้า จนมืดสนิทเหลือวงแสงชมพู -> จันทร์เสี้ยวซีดเส้นเดียวฟันผ่านเป้า เป้ากระเด็น -> จันทร์กลับสว่าง สลายเป็นละอองร่วง ฟ้ากลับ พิษจันทร์มัวทับซ้อน',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: E_DIM },
    { m: 'wxCleave', t0: E_BLINK[0], t1: E_BLINK[1] },
    { m: 'wxCleave', t0: E_BLINK[1], t1: E_BLINK[2] },
    { m: 'wxCleave', t0: E_BLINK[2], t1: E_DISC },
    { m: 'wxRise', t0: E_DISC, t1: E_CRES },
    { m: 'wxBreak', t0: E_CRES, t1: E_RETURN },
    { m: 'wxBuff', t0: E_RETURN, t1: E_END },
  ],
  hits: [E_DIM, ...E_BLINK_HIT.flat(), ...E_BITE_HIT, E_CRES_HIT],
  big: E_CRES_HIT,
  ghosts: [E_BLINK[0] - 0.02, E_BLINK[1] - 0.02, E_BLINK[2] - 0.02, E_CRES + 0.05],
  stepIn: 6,
  cast: [0.05, ECL_T0],
  pre: ECL_T0,
  hit: E_DIM,
  end: E_END,
  afterimage: 'port',
  move: eclipseMove,
  beats: [
    { t: 0.05, th: 'ยกจันทร์' },
    { t: E_DIM, th: 'ฟ้ามืด' },
    { t: E_BLINK[0], th: 'เงาฟัน 1' },
    { t: E_BLINK[1], th: 'เงาฟัน 2' },
    { t: E_BLINK[2], th: 'เงาฟัน 3' },
    { t: E_DISC, th: 'จันทร์เต็มดวง' },
    { t: E_BITE, th: 'กัดจันทร์' },
    { t: E_CRES, th: 'จันทร์เสี้ยว' },
    { t: E_RETURN, th: 'จันทร์คืน' },
  ],
  under(g, s) {
    // The tell: the area ring drawn on round the caster.
    if (s.t >= 0.05 && s.t < E_DIM + 0.2) {
      const k = clamp01((s.t - 0.05) / 0.6);
      level(g, 3);
      if (k >= 1) ringC(g, s.a[0], s.a[1], E_R, V4, true);
      else ringOn(g, s.a, E_R, -Math.PI / 2, k, V4, false);
      g.globalAlpha = 1;
    }
    // The dark: a night disc over the whole field, stars, held until the moon returns.
    const e = since(s.t, E_DIM);
    if (e >= 0 && s.t < E_FADE + 0.3) {
      const c: V = [(s.a[0] + s.f[0]) / 2, s.f[1]];
      const out = since(s.t, E_RETURN);
      const lv = out < 0 ? (e < 2 ? 1 : 2) : out < 4 ? 2 : 1;
      const rr = Math.round(Math.min(E_R + 20, 30 + e * 12));
      // The dark: a solid core, a checker band, a solid pale edge with a black line inside it.
      fieldC(g, c, rr, V1, V0, V4, lv < 2 ? 2 : 3);
      level(g, out < 6 ? 3 : 2);
      for (let i = 0; i < 28; i++) {
        if ((e + i * 3) % 7 === 0) continue;
        mote(g, c[0] + (hash(i, 200) - 0.5) * 2 * E_R, c[1] + (hash(i, 201) - 0.5) * E_R * 0.8, e, i);
      }
      g.globalAlpha = 1;
    }
    // The crescent's mark on the ground, two shock rings.
    const ec = since(s.t, E_CRES_HIT);
    if (ec >= 0 && ec < 16) {
      moonShape(g, [s.f[0], s.f[1] + 1], Math.round(18 + 10 * clamp01(ec / 3)), -Math.PI / 2, 0.5, fade(ec, 8, 12), false, 0.45);
      for (let k = 0; k < 2; k++) {
        const ee = ec - k * 2;
        if (ee < 0 || ee > 8) continue;
        level(g, fade(ee, 3, 6));
        ringC(g, s.f[0], s.f[1], Math.round(E_R * (0.25 + 0.7 * clamp01(ee / 5))), k ? B3 : WH, k === 0);
      }
      g.globalAlpha = 1;
    }
  },
  over(g, s) {
    const { tip } = s.limbs(s.t);
    // The raise: a small moon over the raised tip, growing, a shadow already creeping onto it.
    if (s.t >= 0.1 && s.t < E_DIM + 0.1) {
      const k = clamp01((s.t - 0.1) / 0.6);
      moonDisc(g, [tip[0] + 2, tip[1] - 8 - k * 4], Math.round(3 + 4 * k), k * 0.45, 3);
    }
    const c = strikeAt(s);
    // The blinks: a shadow puff where the body leaves and where it comes out, two dark cuts out of the dark.
    for (let j = 0; j < 3; j++) {
      const eb = since(s.t, E_BLINK[j]!);
      if (eb >= 0 && eb < 6) {
        const b = s.body(s.t);
        level(g, fade(eb, 3, 5));
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * Math.PI * 2 + j;
          const r = 4 + eb * 2.5;
          moonShape(g, [b[0] + Math.cos(a) * r, b[1] - s.chest + Math.sin(a) * r * 0.8], 3, a, 0.6, fade(eb, 3, 5));
        }
        g.globalAlpha = 1;
      }
      for (let h = 0; h < 2; h++) {
        const e = since(s.t, E_BLINK_HIT[j]![h]!);
        if (e < 0 || e >= 5) continue;
        const a0 = [-Math.PI * 0.6, Math.PI * 0.75, -Math.PI * 0.25, Math.PI * 0.35, -Math.PI * 0.85, Math.PI * 1.1][j * 2 + h]!;
        moonCut(g, c[0] + Math.cos(a0 + Math.PI) * 8, c[1] + Math.sin(a0 + Math.PI) * 6, 22, a0, a0 + Math.PI * 0.8, e < 2 ? 7 : 4, fade(e, 2, 4), 0.85, true);
        hitStar(g, s, c, e, 'violet', 1.0);
      }
    }
    // The moon over the foe: rising, bitten three times, eclipsed (a corona), then lit again and crumbling.
    if (s.t >= E_DISC && s.t < E_FADE + 0.3) {
      const k = clamp01((s.t - E_DISC) / 0.35);
      const m = moonAt(s);
      let bite = 0;
      for (let i = 0; i < 3; i++) if (s.t >= E_BITE_HIT[i]!) bite = (i + 1) / 3;
      if (s.t >= E_BITE && bite === 0) bite = 0.12;
      const er = since(s.t, E_RETURN);
      if (er >= 0) bite = Math.max(0, 1 - er * 0.18);
      const lv = er < 8 ? (k < 0.3 ? 2 : 3) : fade(er - 8, 2, 4);
      if (er < 12) moonDisc(g, [m[0], m[1] + (1 - k) * 18], Math.round(10 + 9 * k), bite, lv, true);
      // Shards of shadow falling from each bite onto the foe.
      for (let i = 0; i < 3; i++) {
        const eb = since(s.t, E_BITE_HIT[i]!);
        if (eb < 0 || eb >= 8) continue;
        level(g, fade(eb, 5, 7));
        for (let q = 0; q < 5; q++) {
          const x = m[0] + 10 - q * 5 + hash(q, 210 + i) * 4;
          const y = m[1] + 12 + eb * 6 + hash(q, 220 + i) * 8;
          moonShape(g, [x, y], 3, Math.PI / 2 + q, 0.55, fade(eb, 5, 7));
        }
        g.globalAlpha = 1;
        hitStar(g, s, c, eb, 'violet', 0.9);
      }
      // The moon crumbling into falling motes as it comes back.
      riseMotes(g, [m[0], m[1] + 10], 40, er - 4, 14, 230, 16, false, -2.2);
    }
    // The crescent: one pale sliver drawn off the eclipsed moon down through the foe, the biggest cut.
    bladeArc(g, s, E_CRES - 0.05, E_CRES_HIT + 0.15, 'violet', 5);
    const ec = since(s.t, E_CRES_HIT);
    if (ec >= -2 && ec < 0) {
      const m = moonAt(s);
      moonShape(g, [m[0] + 6, m[1]], 18, 0, 0.82, 3);
    }
    if (ec >= 0 && ec < 7) moonCut(g, c[0] - 24, c[1] - 10, 50, -Math.PI * 0.5, Math.PI * 0.42, ec < 2 ? 11 : 6, fade(ec, 3, 5), 1);
    shards(g, c, ec - 1, 12, 240, 3.8, 14);
    hitStar(g, s, c, ec, 'violet', 2);
    hitStar(g, s, c, since(s.t, E_DIM), 'violet', 0.8);
    // The aftermath: the stacked blight on the foe.
    const ef = since(s.t, E_BLINK_HIT[0][0]);
    if (ef >= 2) {
      let n = 0;
      for (const hs of E_BLINK_HIT) if (s.t >= hs[0]) n++;
      if (s.t >= E_BITE_HIT[0]) n++;
      if (s.t >= E_CRES_HIT) n++;
      const out = since(s.t, E_FADE);
      blight(g, s, n, out < 0 ? 3 : fade(out, 4, 6));
    }
  },
};

/** The listening-blade moves, hand-made (each its own shape, motion and timing; one palette and motif family). */
export const MOON_FX: readonly ArtFx[] = [moon, veilFx, longing, step, night, kiss, mirror, eclipse];

/** The two long moves' page times (s), for fx/longMoon.ts to pin them to the live beats. */
export const MOON_KISS_T = {
  start: KISS_T0,
  turn: K_TURN,
  dip: K_DIP,
  touch: K_TOUCH,
  spin: K_SPIN,
  bow: K_BOW,
  stance: K_STANCE,
  end: K_END,
} as const;
export const MOON_ECLIPSE_T = {
  start: ECL_T0,
  blink: E_BLINK,
  disc: E_DISC,
  bite: E_BITE,
  cres: E_CRES,
  ret: E_RETURN,
  fade: E_FADE,
  end: E_END,
} as const;
